import React from "react"
import {
  BooleanInput,
  ChipField,
  CreateController,
  CreateView,
  Datagrid,
  DateField,
  Edit,
  EditButton,
  FileInput,
  FormDataConsumer,
  FunctionField,
  ImageField,
  Labeled,
  List,
  NumberInput,
  RadioButtonGroupInput,
  ReferenceArrayField,
  ReferenceArrayInput,
  required,
  SelectArrayInput,
  ShowController,
  ShowView,
  SimpleForm,
  SimpleShowLayout,
  SingleFieldList,
  TextField,
  TextInput
} from "react-admin"

import Link from "@material-ui/core/Link"

import {
  ShowActionsToolbar,
  CreateActionsToolbar,
  EditActionsToolbar,
  BGBooleanField
} from "../common"
import { Workout } from "./types"
import {
  apiUploads,
  WorkoutTitle,
  WorkoutFilter,
  ShortFileField
} from "./workoutConstants"
import { ZoneMatrixView, ZoneMatrixInputs } from "./workoutZoneMatrix"

export const WorkoutList: React.FC = (props) => {
  return (
    <List filters={<WorkoutFilter />} bulkActionButtons={false} {...props}>
      <Datagrid rowClick="show">
        <TextField source="name" />
        <FunctionField
          label="Type"
          render={(record: any) =>
            record && record.type === "zone" ? "ZONE" : "Single"
          }
        />
        <ReferenceArrayField
          label="Sub-Categories"
          reference="workout-categories"
          source="categories"
        >
          <SingleFieldList>
            <ChipField source="name" />
          </SingleFieldList>
        </ReferenceArrayField>
        <FunctionField
          label="Filename"
          style={{ fontSize: "0.7rem" }}
          render={(record: any) => {
            if (record && record.type === "zone") return "(zone — matrix)"
            const f = record && record.filename ? record.filename : ""
            return f.length > 40
              ? f.slice(0, 20) + " ... " + f.slice(-20)
              : f
          }}
        />
        <FunctionField
          label="Duration"
          render={(record: any) =>
            record && record.type === "zone" ? "—" : record.duration
          }
        />
        <BGBooleanField source="enabled" />
        <EditButton />
      </Datagrid>
    </List>
  )
}

export const WorkoutShow: React.FC = (props) => (
  <ShowController {...props}>
    {(controllerProps) => {
      const record = controllerProps.record as any
      const isZone = record && record.type === "zone"
      return (
        <ShowView
          {...controllerProps}
          actions={
            <ShowActionsToolbar>
              <WorkoutTitle record={record as unknown as Workout} />
            </ShowActionsToolbar>
          }
          title={<WorkoutTitle />}
        >
          <SimpleShowLayout>
            <TextField label="Id" source="id" />
            <FunctionField
              label="Type"
              render={(r: any) => (r.type === "zone" ? "ZONE" : "Single")}
            />
            <TextField source="name" />
            <TextField fullWidth source="description" />
            <ReferenceArrayField
              label="Sub-Categories"
              reference="workout-categories"
              source="categories"
            >
              <SingleFieldList>
                <ChipField source="name" />
              </SingleFieldList>
            </ReferenceArrayField>

            {!isZone && (
              <FunctionField
                label="Mac 1 & Mac 2 file"
                render={(r: any) => {
                  const f = r.assets && r.assets.mac12 ? r.assets.mac12.filename : null
                  return f ? (
                    <Link href={`${apiUploads}/${f}`} target="_blank">
                      {f}
                    </Link>
                  ) : (
                    "—"
                  )
                }}
              />
            )}
            {!isZone && (
              <FunctionField
                label="Mac 3 file"
                render={(r: any) => {
                  const f = r.assets && r.assets.mac3 ? r.assets.mac3.filename : r.filename
                  return f ? (
                    <Link href={`${apiUploads}/${f}`} target="_blank">
                      {f}
                    </Link>
                  ) : (
                    "—"
                  )
                }}
              />
            )}

            {isZone && (
              <FunctionField
                label="Body Part"
                render={(r: any) =>
                  r.body_part ? r.body_part.name || "—" : "—"
                }
              />
            )}
            {isZone && (
              <FunctionField
                label="Body Part Image"
                render={(r: any) =>
                  r.body_part && r.body_part.image ? (
                    <img
                      src={r.body_part.image}
                      alt="body part"
                      style={{ maxWidth: 200, borderRadius: 8, marginTop: 4 }}
                    />
                  ) : (
                    "—"
                  )
                }
              />
            )}
            {isZone && (
              <FunctionField
                label="Zone files (matrix)"
                render={(r: any) => <ZoneMatrixView cells={r.cells || []} />}
              />
            )}

            <Labeled label="Enabled">
              <BGBooleanField source="enabled" />
            </Labeled>
            <DateField source="created" showTime />
            <DateField source="modified" showTime />
          </SimpleShowLayout>
        </ShowView>
      )
    }}
  </ShowController>
)

export const WorkoutCreate: React.FC = (props) => (
  <CreateController {...props}>
    {(controllerProps) => (
      <CreateView {...controllerProps} actions={<CreateActionsToolbar />}>
        <SimpleForm redirect="list">
          <RadioButtonGroupInput
            source="type"
            label="Workout Type"
            defaultValue="single"
            choices={[
              { id: "single", name: "Single Workout" },
              { id: "zone", name: "Zones" }
            ]}
            validate={required()}
          />
          <TextInput fullWidth source="name" validate={required()} />
          <TextInput
            fullWidth
            multiline
            source="description"
            validate={required()}
          />
          <BooleanInput source="enabled" defaultValue={true} />
          <ReferenceArrayInput
            fullWidth
            source="categories"
            label="Sub-Categories"
            reference="workout-categories"
            filter={{ is_parent: false }}
            perPage={1000}
            isRequired
            validate={[required()]}
          >
            <SelectArrayInput optionText="name" />
          </ReferenceArrayInput>

          <FormDataConsumer>
            {({ formData }) =>
              formData.type === "zone" ? (
                <>
                  <TextInput
                    source="body_part.name"
                    label="Body Part Name"
                    fullWidth
                  />
                  <FileInput
                    source="bodyPartImage"
                    label="Body Part Image"
                    accept="image/*"
                  >
                    <ImageField source="src" title="title" />
                  </FileInput>
                  <ZoneMatrixInputs />
                </>
              ) : (
                <>
                  <NumberInput
                    source="duration"
                    label="Duration (Minutes)"
                    defaultValue={60}
                    validate={required()}
                  />
                  <FileInput
                    source="mac12File"
                    label="Mac 1 & Mac 2 Workout Asset (.csv)"
                  >
                    <ShortFileField source="src" title="title" />
                  </FileInput>
                  <FileInput
                    source="mac3File"
                    label="Mac 3 Workout Asset (.csv)"
                  >
                    <ShortFileField source="src" title="title" />
                  </FileInput>
                </>
              )
            }
          </FormDataConsumer>
        </SimpleForm>
      </CreateView>
    )}
  </CreateController>
)

export const WorkoutEdit: React.FC = (props) => (
  <Edit
    actions={<EditActionsToolbar />}
    title={<WorkoutTitle />}
    mutationMode="pessimistic"
    {...props}
  >
    <SimpleForm redirect="list">
      <TextInput disabled source="id" />
      <FunctionField
        label="Type"
        render={(r: any) => (r.type === "zone" ? "ZONE" : "Single")}
      />
      <TextInput fullWidth source="name" validate={required()} />
      <TextInput
        fullWidth
        multiline
        source="description"
        validate={required()}
      />
      <ReferenceArrayInput
        fullWidth
        source="categories"
        label="Sub-Categories"
        reference="workout-categories"
        filter={{ is_parent: false }}
        perPage={1000}
        isRequired
        validate={[required()]}
      >
        <SelectArrayInput optionText="name" />
      </ReferenceArrayInput>
      <BooleanInput source="enabled" />

      <FormDataConsumer>
        {({ formData }) =>
          formData.type === "zone" ? (
            <>
              <TextInput
                source="body_part.name"
                label="Body Part Name"
                fullWidth
              />
              <FunctionField
                label="Current Body Part Image"
                render={(r: any) =>
                  r.body_part && r.body_part.image ? (
                    <img
                      src={r.body_part.image}
                      alt="current body part"
                      style={{ maxWidth: 150, borderRadius: 8, marginBottom: 8 }}
                    />
                  ) : (
                    <span style={{ color: "#888" }}>No image yet</span>
                  )
                }
              />
              <FileInput
                source="bodyPartImage"
                label="Replace Body Part Image"
                accept="image/*"
              >
                <ImageField source="src" title="title" />
              </FileInput>
              <div style={{ color: "#6E6E6E", fontSize: "0.8rem", margin: "8px 0" }}>
                Green ✓ means a file is already saved in that cell. Upload a new
                file only in the cells you want to change — the rest stay as they are.
              </div>
              <ZoneMatrixInputs
                existingCells={formData.cells}
                formData={formData}
              />
            </>
          ) : (
            <>
              <NumberInput
                source="duration"
                label="Duration (Minutes)"
                defaultValue={60}
                validate={required()}
              />
              <div style={{ width: "100%", marginTop: 16, marginBottom: 8 }}>
                <div style={{ fontSize: "0.75rem", color: "rgba(0, 0, 0, 0.54)" }}>
                  Current Mac 1 & Mac 2 file
                </div>
                <div style={{ marginTop: 4 }}>
                  {formData.assets && formData.assets.mac12 && formData.assets.mac12.filename ? (
                    <Link
                      href={`${apiUploads}/${formData.assets.mac12.filename}`}
                      target="_blank"
                    >
                      {formData.assets.mac12.filename}
                    </Link>
                  ) : (
                    "—"
                  )}
                </div>
              </div>
              <FileInput
                source="mac12File"
                label="Replace Mac 1 & Mac 2 (.csv)"
              >
                <ShortFileField source="src" title="title" />
              </FileInput>
              <div style={{ width: "100%", marginTop: 16, marginBottom: 8 }}>
                <div style={{ fontSize: "0.75rem", color: "rgba(0, 0, 0, 0.54)" }}>
                  Current Mac 3 file
                </div>
                <div style={{ marginTop: 4 }}>
                  {formData.assets && formData.assets.mac3 && formData.assets.mac3.filename ? (
                    <Link
                      href={`${apiUploads}/${formData.assets.mac3.filename}`}
                      target="_blank"
                    >
                      {formData.assets.mac3.filename}
                    </Link>
                  ) : (
                    "—"
                  )}
                </div>
              </div>
              <FileInput source="mac3File" label="Replace Mac 3 (.csv)">
                <ShortFileField source="src" title="title" />
              </FileInput>
            </>
          )
        }
      </FormDataConsumer>
    </SimpleForm>
  </Edit>
)